import { useState } from 'react';
import { fullName } from '@pickem/shared';
import { Card, Field, ErrorBanner, SectionTitle } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatPhone } from '../lib/format';

export function AccountPage() {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState(user?.phone ? formatPhone(user.phone) : '');
  const [phoneMsg, setPhoneMsg] = useState('');
  const [phoneErr, setPhoneErr] = useState('');
  const [phoneBusy, setPhoneBusy] = useState(false);

  async function savePhone(e: React.FormEvent) {
    e.preventDefault();
    setPhoneMsg('');
    setPhoneErr('');
    setPhoneBusy(true);
    try {
      const digits = phone.replace(/\D/g, '');
      await api.patch('/auth/me', { phone: digits.length ? digits : null });
      await refresh();
      setPhoneMsg('Phone updated.');
    } catch (err) {
      setPhoneErr(err instanceof ApiError ? err.message : 'Unable to update phone.');
    } finally {
      setPhoneBusy(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMsg('');
    setError('');
    setBusy(true);
    try {
      await api.post('/auth/change-password', { currentPassword: current, newPassword: next });
      setMsg('Your password has been updated.');
      setCurrent('');
      setNext('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unable to change password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-md">
      <SectionTitle>Account</SectionTitle>
      <Card className="mb-4">
        <dl className="space-y-1 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted">Name</dt>
            <dd className="font-medium">{user ? fullName(user) : ''}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted">Email</dt>
            <dd className="font-medium">{user?.email}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted">Role</dt>
            <dd className="font-medium">{user?.isSuperAdmin ? 'Super Admin' : 'Member'}</dd>
          </div>
        </dl>
      </Card>

      <Card className="mb-4">
        <h3 className="mb-3 font-display text-2xl uppercase text-ink">Phone</h3>
        <form onSubmit={savePhone} noValidate>
          {phoneErr && (
            <div className="mb-3">
              <ErrorBanner message={phoneErr} />
            </div>
          )}
          {phoneMsg && (
            <div className="mb-3 rounded border border-success/30 bg-success/5 px-4 py-2 text-sm">
              {phoneMsg}
            </div>
          )}
          <Field label="Phone number" htmlFor="phone" hint="Optional. US 10-digit number.">
            <input
              id="phone"
              type="tel"
              className="input"
              autoComplete="tel"
              placeholder="(208) 555-1234"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Field>
          <button type="submit" className="btn-primary" disabled={phoneBusy}>
            {phoneBusy ? 'Saving…' : 'Save phone'}
          </button>
        </form>
      </Card>

      <Card>
        <h3 className="mb-3 font-display text-2xl uppercase text-ink">Change password</h3>
        <form onSubmit={onSubmit} noValidate>
          {error && (
            <div className="mb-3">
              <ErrorBanner message={error} />
            </div>
          )}
          {msg && (
            <div className="mb-3 rounded border border-success/30 bg-success/5 px-4 py-2 text-sm">
              {msg}
            </div>
          )}
          <Field label="Current password" htmlFor="current">
            <input
              id="current"
              type="password"
              className="input"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </Field>
          <Field label="New password" htmlFor="next" hint="At least 8 characters.">
            <input
              id="next"
              type="password"
              className="input"
              autoComplete="new-password"
              minLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
            />
          </Field>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Update password'}
          </button>
        </form>
      </Card>
    </div>
  );
}
